import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import {
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  // TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PersonCelebrate from '../../../ui/icons/PersonCelebrate';
import PieChart from '../../../ui/icons/PieChart';
import CurrencyField from '../../../ui/CurrencyField/CurrencyField';

interface Participant {
  id: string;
  name: string;
  amount?: number;
  portions?: number;
}

interface ReAllocateMoneyProps {
  totalAmount: number;
  participants: Participant[];
  onClose: () => void;
  onComplete: (allocations: Participant[]) => void;
  currentUserId?: string;
}

enum AllocationMethod {
  BY_AMOUNT = 'amount',
  BY_PORTIONS = 'portions',
}

// Common styles used across the component
const styles = {
  textField: {
    fontSize: 'large',
    fontWeight: 'bold',
    textAlign: 'right',
    '& input': {
      textAlign: 'right',
      textDecoration: 'underline',
      textUnderlineOffset: '0.1em',
    },
  },
  actionButton: {
    borderRadius: 20,
    px: 3,
    py: 0.75,
  },
  completeButton: {
    borderRadius: 28,
    py: 1.5,
  },
  participantItem: {
    alignItems: 'center',
    justifyContent: 'space-between',
    py: 1.5,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
};

const ReAllocateMoney = ({
  totalAmount,
  participants = [],
  onClose,
  onComplete,
  currentUserId,
}: ReAllocateMoneyProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'loan']);

  const [allocationType, setAllocationType] = useState<AllocationMethod>(
    AllocationMethod.BY_AMOUNT
  );
  // Combine allocations and lockedItems in a single state to avoid stale closure bugs.
  // Reading lockedItems inside setState(fn) from a separate state would capture
  // the closure value at render time, not the latest value at update time.
  const [state, setState] = useState<{
    allocations: Participant[];
    lockedItems: Record<string, boolean>;
  }>(() => {
    const equalShare = totalAmount / participants.length;
    return {
      allocations: participants.map((p) => ({
        ...p,
        amount: p.amount !== undefined ? p.amount : Math.floor(equalShare),
        portions: p.portions !== undefined ? p.portions : 1,
      })),
      lockedItems: {},
    };
  });
  const allocations = state.allocations;
  const lockedItems = state.lockedItems;

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: AllocationMethod
  ) => {
    setAllocationType(newValue);
  };

  const handleAmountAllocationChange = (
    id: string,
    value: number | undefined
  ) => {
    setState((prevState) => {
      const { allocations: prev, lockedItems: prevLockedItems } = prevState;

      // Update the specified participant's amount
      let newAllocations = prev.map((p) =>
        p.id === id ? { ...p, amount: value } : p
      );

      // Calculate new locked items immediately (not async)
      let newLockedItems = { ...prevLockedItems, [id]: true };

      // Check if all items are locked
      const allLocked = newAllocations.every((p) => newLockedItems[p.id]);

      // If all items are locked, unlock all of them except the current one
      if (allLocked) {
        newLockedItems = { [id]: true };
      }

      // Calculate remaining amount to distribute (using newLockedItems, not stale lockedItems)
      const remainingAmount =
        totalAmount -
        newAllocations.reduce(
          (sum, p) =>
            p.id === id || newLockedItems[p.id] ? sum + (p.amount || 0) : sum,
          0
        );

      // Count unlocked participants (excluding the current one, using newLockedItems)
      const unlockedParticipants = newAllocations.filter(
        (p) => p.id !== id && !newLockedItems[p.id]
      );

      // handle case when total is exceeded
      if (remainingAmount <= 0) {
        // If the remaining amount is negative, cap it at 0
        newAllocations = newAllocations.map((p) => {
          if (p.id === id || newLockedItems[p.id]) {
            return p;
          }
          return { ...p, amount: 0 };
        });
      }

      // If there are unlocked participants and remaining amount > 0, distribute it
      if (unlockedParticipants.length > 0 && remainingAmount > 0) {
        const baseAmount = Math.floor(
          remainingAmount / unlockedParticipants.length
        );
        const remainder = remainingAmount % unlockedParticipants.length;

        // Distribute the remaining amount among unlocked participants
        let remainderDistributed = 0;
        newAllocations = newAllocations.map((p) => {
          // Skip the current participant and locked ones
          if (p.id === id || newLockedItems[p.id]) {
            return p;
          }

          // Distribute remainder to the first few unlocked participants
          let extraAmount = 0;
          if (remainderDistributed < remainder) {
            extraAmount = 1;
            remainderDistributed++;
          }

          return { ...p, amount: baseAmount + extraAmount };
        });
      }

      const totalAllocated = newAllocations.reduce(
        (sum, p) => sum + (p.amount || 0),
        0
      );
      if (totalAllocated > totalAmount) {
        newAllocations = newAllocations.map((p) => {
          if (p.id === id) {
            // If this is the current participant, set to max allowed amount
            return {
              ...p,
              amount: Math.max(
                0,
                totalAmount - (totalAllocated - (p.amount || 0))
              ),
            };
          }
          return p;
        });
        // Reset locked items since we adjusted amounts
        newLockedItems = {};
      }

      return { allocations: newAllocations, lockedItems: newLockedItems };
    });
  };

  const handlePortionsAllocationChange = (
    id: string,
    value: number | undefined
  ) => {
    // unlock the item when changing portions
    setState((prevState) => {
      const newAllocations = prevState.allocations.map((p) =>
        p.id === id ? { ...p, portions: value } : p
      );
      // When changing portions, recalculate all amounts based on the new portions distribution
      return {
        allocations: calculateAllocationsByPortions(newAllocations),
        lockedItems: {},
      };
    });
  };

  const handleEvenDistribution = () => {
    // Distribute evenly by amount
    const participantsCount = participants.length;
    const baseAmount = Math.floor(totalAmount / participantsCount);
    const remainder = totalAmount % participantsCount;

    setState((prevState) => ({
      allocations: prevState.allocations.map((p, index) => {
        // Give all remainder to the first participant
        const extraAmount = index === 0 ? remainder : 0;
        return { ...p, amount: baseAmount + extraAmount, portions: 1 };
      }),
      // Unlock all participants when distributing evenly
      lockedItems: {},
    }));
  };

  // Calculate allocations based on portions and distribute total amount
  const calculateAllocationsByPortions = (allocations: Participant[]) => {
    const totalPortions = allocations.reduce(
      (sum, p) => sum + (p.portions || 0),
      0
    );
    if (totalPortions === 0) return allocations;

    const amountPerPortion = totalAmount / totalPortions;

    return allocations.map((p) => ({
      ...p,
      amount: Math.floor(amountPerPortion * (p.portions || 0)),
    }));
  };

  return (
    <Stack sx={{ height: '100%', width: '100%' }}>
      {/* Header */}
      <Stack
        direction="row"
        sx={{
          padding: 2,
          alignItems: 'center',
        }}
      >
        <IconButton onClick={onClose} sx={{ padding: 0 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h6"
          sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 500 }}
        >
          {t('reallocate.totalToAllocate', 'Tổng tiền cần chia', {
            ns: 'loan',
          })}
        </Typography>
      </Stack>

      {/* Total amount display */}
      <Stack sx={{ padding: 2, textAlign: 'center' }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}
        >
          {new Intl.NumberFormat('vi-VN').format(totalAmount)}
        </Typography>
      </Stack>

      {/* Tabs for allocation methods */}
      <Stack>
        <Tabs
          value={allocationType}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3,
            },
          }}
        >
          <Tab
            label={t('reallocate.byAmount', 'Theo số tiền', { ns: 'loan' })}
            value={AllocationMethod.BY_AMOUNT}
            sx={{
              fontWeight:
                allocationType === AllocationMethod.BY_AMOUNT
                  ? 'bold'
                  : 'normal',
              textTransform: 'none',
            }}
          />
          <Tab
            label={t('reallocate.byPortions', 'Theo khẩu phần', { ns: 'loan' })}
            value={AllocationMethod.BY_PORTIONS}
            sx={{
              fontWeight:
                allocationType === AllocationMethod.BY_PORTIONS
                  ? 'bold'
                  : 'normal',
              textTransform: 'none',
            }}
          />
        </Tabs>
      </Stack>

      {/* Description and visual content */}
      <Stack
        sx={{
          p: 3,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {allocationType === AllocationMethod.BY_AMOUNT ? (
          <>
            {/* Money equal distribution icon */}
            <Stack
              mb={2}
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={theme.spacing(2)}
            >
              <PersonCelebrate
                width="101"
                height="100"
                viewBox="0 0 101 100"
                fontSize={100}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {t(
                  'reallocate.defaultEqualDistribution',
                  'Mặc định chia đều, nhưng bạn có thể tùy chỉnh cho từng người',
                  { ns: 'loan' }
                )}
              </Typography>
            </Stack>
            <Stack
              width="100%"
              direction="row"
              spacing={theme.spacing(2)}
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleEvenDistribution}
                size="small"
                sx={styles.actionButton}
              >
                {t('reallocate.distributeEven', 'Chia đều', { ns: 'loan' })}
              </Button>
            </Stack>
          </>
        ) : (
          <>
            {/* Pie chart icon for portions */}
            <Stack
              mb={2}
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={theme.spacing(2)}
            >
              <PieChart
                width="101"
                height="100"
                viewBox="0 0 101 100"
                fontSize={100}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                whiteSpace={'pre-line'}
              >
                {t(
                  'reallocate.portionExplanation',
                  'Chia theo khẩu phần của mỗi người trả tiền.\nVí dụ 1 người trả cho gia đình 3 người sẽ là 3 phần.',
                  { ns: 'loan' }
                )}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>

      {/* List of participants with allocation inputs */}
      <Stack
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 2,
        }}
      >
        {allocations.map((participant) => (
          <Stack
            key={participant.id}
            direction="row"
            sx={styles.participantItem}
          >
            <Stack direction="row" alignItems="center">
              <Stack
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mr: 2,
                }}
              >
                <PersonIcon />
              </Stack>
              <Typography variant="body1">
                {participant.name}
                {participant.id === currentUserId &&
                  ` (${t('common.you', '(tôi)', { ns: 'common' })})`}
              </Typography>
            </Stack>

            {allocationType === AllocationMethod.BY_AMOUNT ? (
              <CurrencyField
                name="amount"
                value={participant.amount?.toLocaleString('vi-VN') || ''}
                onConfirm={(value) => {
                  const numericValue = value.replace(/\D/g, '');
                  handleAmountAllocationChange(
                    participant.id,
                    numericValue ? Number(numericValue) : undefined
                  );
                }}
                selectOnFocus
                highlightOnFocus
                slotProps={{
                  input: {
                    disableUnderline: true,
                    sx: {
                      ...styles.textField,
                      '& input': {
                        ...styles.textField['& input'],
                        textDecorationThickness: lockedItems[participant.id]
                          ? '0.15em'
                          : '0.05em',
                      },
                    },
                  },
                }}
                variant="standard"
                fontSize="16px"
                sx={{width: '100px'}}
              />
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
              >
                <CurrencyField
                  name="amount"
                  style={{ marginTop: 0 }}
                  value={participant.portions || ''}
                  onConfirm={(value) =>
                    handlePortionsAllocationChange(
                      participant.id,
                      value ? Number(value) : undefined
                    )
                  }
                  selectOnFocus
                  highlightOnFocus
                  variant="standard"
                  type="number"
                  slotProps={{
                    input: {
                      disableUnderline: true,
                      sx: {
                        width: 'fit-content',
                        ...styles.textField,
                      },
                    },
                  }}
                  fontSize="16px"
                  sx={{width: '50px'}}
                />
                <Typography sx={{ ml: 1, color: 'text.secondary' }}>
                  {t('reallocate.portion', 'phần', { ns: 'loan' })}
                </Typography>
              </Stack>
            )}
          </Stack>
        ))}
      </Stack>

      {/* Complete button */}
      <Stack sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          onClick={() => onComplete(allocations)}
          sx={styles.completeButton}
        >
          {t('common:done', 'Xong')}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ReAllocateMoney;
